import { useEffect, useRef } from 'react';

/**
 * @param {Object} params
 * @param {Object} params.defaultFilters - default filters (from backend)
 * @param {Object} params.filter - local filter (date range)
 * @param {Function} params.setFilter - setState for filter
 * @param {Array} params.extraFilters - array of filters
 * @param {Function} params.setExtraFilters - setState for filters
 * @param {Function} params.getActiveFilters - function to get active filters
 * @param {Object} params.body - request body (useMemo)
 * @param {Function} [params.onApplyFilters] - callback for applying filters (save to backend)
 */
const useDefaultFiltersSync = ({
	defaultFilters,
	filter,
	setFilter,
	extraFilters,
	setExtraFilters,
	getActiveFilters,
	body,
	onApplyFilters
}) => {
	const isSyncingRef = useRef(false);
	const prevFiltersRef = useRef({ filter: null, extraFilters: null });
	const prevBodyRef = useRef();

	useEffect(() => {
		if (!defaultFilters) return;
		const filtersChanged =
			defaultFilters.from !== filter.start ||
			defaultFilters.to !== filter.end ||
			JSON.stringify(defaultFilters.filter ?? []) !== JSON.stringify(getActiveFilters(extraFilters) ?? []);
		if (!filtersChanged) return;

		isSyncingRef.current = true;
		if (defaultFilters.from && defaultFilters.to) {
			setFilter({ start: defaultFilters.from, end: defaultFilters.to });
		}
		if (Array.isArray(defaultFilters.filter) && defaultFilters.filter.length > 0) {
			setExtraFilters((prevFilters) =>
				prevFilters.map((filterItem) => {
					const saved = defaultFilters.filter.find((f) => f.key[0] === filterItem.filterValues.key[0]);
					return saved ? { ...filterItem, filterValues: saved } : filterItem;
				})
			);
		}
		prevFiltersRef.current = {
			filter: { start: defaultFilters.from, end: defaultFilters.to },
			extraFilters: (defaultFilters.filter ?? []).map((f) => ({ ...f }))
		};
	}, [defaultFilters, getActiveFilters, setFilter, setExtraFilters]);

	useEffect(() => {
		if (isSyncingRef.current) {
			isSyncingRef.current = false;
			return;
		}
		const prevBody = prevBodyRef.current;
		const bodyChanged = JSON.stringify(prevBody) !== JSON.stringify(body);
		prevBodyRef.current = body;
		if (!bodyChanged) return;
		if (onApplyFilters) {
			onApplyFilters(body);
		}
	}, [body, onApplyFilters]);
};

export default useDefaultFiltersSync;
